import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Package, Tag, DollarSign, Truck, Settings, AlertTriangle,
  ChevronLeft, Edit, Trash2, MoreVertical, Image, Download,
  ChevronDown, ChevronUp, Eye, Copy, Share2, Clock, User,
  Barcode, Scale, Box, HelpCircle, Loader2, Upload, FileText, Plus
} from 'lucide-react'
import { cn, formatCurrency, formatNumber, formatDate } from '../../lib/utils'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { productsApi, queryKeys } from '../../lib/api'
import { useAuthStore , usePermissions } from '../../stores/authStore'
import { Button } from '../../components/ui/Button'
import { Badge } from '../../components/ui/Badge'
import { Modal } from '../../components/ui/Modal'
import { Dropdown } from '../../components/ui/Dropdown'
import { Skeleton } from '../../components/ui/Skeleton'
import { Tabs, TabList, Tab, TabPanels, TabPanel } from '../../components/ui/Tabs'
import { useUIStore } from '../../stores/uiStore'
import toast from 'react-hot-toast'

const tabs = [
  { id: 'overview', label: 'نمای کلی', icon: Package },
  { id: 'variants', label: 'تنوع‌ها', icon: Tag },
  { id: 'stock', label: 'موجودی', icon: Truck },
  { id: 'pricing', label: 'قیمت‌ها', icon: DollarSign },
  { id: 'movements', label: 'حرکات', icon: Clock },
  { id: 'documents', label: 'اسناد', icon: HelpCircle },
  { id: 'history', label: 'تاریخچه', icon: Clock },
]

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>()
  const { hasPermission } = usePermissions()
  const { modals, openModal, closeModal } = useUIStore()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState('overview')
  const [deletedId, setDeletedId] = useState<string | null>(null)

  const { data: product, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.product(id!),
    queryFn: () => productsApi.get(id!),
    enabled: !!id,
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => productsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.products() })
      toast.success('کالا حذف شد')
      closeModal('delete-confirm')
      window.history.back()
    },
    onError: (err: any) => toast.error(err.response?.data?.detail || 'حذف ناموفق بود'),
  })

  const getRowDropdownItems = (variant: any) => [
    { label: 'مشاهده جزئیات', icon: Eye, onClick: () => {} },
    { label: 'ویرایش', icon: Edit, onClick: () => {}, disabled: !hasPermission('product', 'update') },
    { label: 'کپی SKU', icon: Copy, onClick: () => navigator.clipboard.writeText(variant.sku) },
    { type: 'divider' },
    { label: 'حذف', icon: Trash2, className: 'text-danger-600', onClick: () => { setDeletedId(variant.id); openModal('delete-confirm') }, disabled: !hasPermission('product', 'delete') },
  ]

  if (isLoading) {
    return (
      <div className="p-6">
        <Skeleton variant="card" />
        <Skeleton variant="card" className="mt-4" />
        <Skeleton variant="card" className="mt-4" />
      </div>
    )
  }

  if (error || !product) {
    return (
      <div className="p-12 text-center">
        <AlertTriangle className="w-16 h-16 text-danger-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">کالا یافت نشد</h2>
        <p className="text-neutral-500 dark:text-neutral-400 mb-4">کالای درخواستی وجود ندارد یا حذف شده است</p>
        <Link to="/inventory/products">
          <Button><ChevronLeft className="w-4 h-4 ml-1" /> بازگشت به لیست</Button>
        </Link>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Link to="/inventory/products" className="p-2 rounded-lg text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors">
              <ChevronLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                {product.images?.[0] ? (
                  <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover rounded-xl" />
                ) : (
                  <Package className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                )}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">{product.name}</h1>
                <p className="text-sm text-neutral-500 dark:text-neutral-400 font-mono">{product.sku}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 mt-2">
              <Badge variant={product.is_active ? 'success' : 'neutral'} className="text-sm">
                {product.is_active ? 'فعال' : 'غیرفعال'}
              </Badge>
              <Badge variant={product.type === 'variant' ? 'primary' : product.type === 'bundle' ? 'info' : product.type === 'service' ? 'warning' : product.type === 'digital' ? 'danger' : 'neutral'} className="text-sm">
                {product.type === 'simple' ? 'ساده' : product.type === 'variant' ? 'متغییر' : product.type === 'bundle' ? 'بندل' : product.type === 'service' ? 'خدماتی' : 'دیجیتال'}
              </Badge>
              {product.category && <Badge variant="neutral" className="text-sm">{product.category.name}</Badge>}
              {product.brand && <Badge variant="neutral" className="text-sm">{product.brand.name}</Badge>}
            </div>
          </div>
        </div>

          <div className="flex items-center gap-3">
            <Dropdown
              trigger={
                <Button variant="outline">
                  <MoreVertical className="w-4 h-4" />
                  منو
                </Button>
              }
              items={[
                { label: 'ویرایش کالا', icon: Edit, onClick: () => window.open(`/inventory/products/${product.id}/edit`, '_blank'), disabled: !hasPermission('product', 'update') },
                { label: 'کپی SKU', icon: Copy, onClick: () => navigator.clipboard.writeText(product.sku) },
                { label: 'دانلود بارکد', icon: Barcode, onClick: () => {} },
                { type: 'divider' },
                { label: 'حذف کالا', icon: Trash2, className: 'text-danger-600', onClick: () => openModal('delete-confirm'), disabled: !hasPermission('product', 'delete') },
              ]}
            />
            <Button variant="outline">
              <Download className="w-4 h-4 ml-1" />
              خروجی
            </Button>
          </div>
        </motion.div>

      {/* Stats Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
      >
        <div className="card p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">موجودی کل</p>
              <p className="text-2xl font-bold text-neutral-900 dark:text-white mt-1">
                {formatNumber(product.current_stock || 0)}
              </p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
              <Box className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
          </div>
          {product.min_stock > 0 && product.current_stock <= product.min_stock && (
            <p className="mt-2 text-sm text-warning-600 dark:text-warning-400 flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5" /> زیر حداقل ({product.min_stock})
            </p>
          )}
        </div>

        <div className="card p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">قیمت فروش</p>
              <p className="text-2xl font-bold text-neutral-900 dark:text-white mt-1">
                {formatCurrency(product.sale_price || 0)}
              </p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-success-100 dark:bg-success-900/30 flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-success-600 dark:text-success-400" />
            </div>
          </div>
        </div>

        <div className="card p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">قیمت خرید</p>
              <p className="text-2xl font-bold text-neutral-900 dark:text-white mt-1">
                {formatCurrency(product.cost_price || 0)}
              </p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-warning-100 dark:bg-warning-900/30 flex items-center justify-center">
              <Scale className="w-6 h-6 text-warning-600 dark:text-warning-400" />
            </div>
          </div>
        </div>

        <div className="card p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">تنوع‌ها</p>
              <p className="text-2xl font-bold text-neutral-900 dark:text-white mt-1">
                {product.variants?.length || (product.type === 'simple' ? 1 : 0)}
              </p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-info-100 dark:bg-info-900/30 flex items-center justify-center">
              <Tag className="w-6 h-6 text-info-600 dark:text-info-400" />
            </div>
          </div>
        </div>
      </motion.div>

      {/* Tabs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="card overflow-hidden"
      >
        <Tabs value={activeTab} onValueChange={setActiveTab} className="border-b border-neutral-200 dark:border-neutral-800">
          <TabList className="flex gap-1 p-1">
            {tabs.map((tab) => (
              <Tab key={tab.id} value={tab.id} className="flex items-center gap-1.5">
                <tab.icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </Tab>
            ))}
          </TabList>

          <TabPanels className="p-5">
            {/* Overview Tab */}
            <TabPanel value="overview" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Images */}
                <div className="lg:col-span-1 space-y-4">
                  <h3 className="font-semibold text-neutral-900 dark:text-white">تصاویر</h3>
                  <div className="aspect-square rounded-xl bg-neutral-100 dark:bg-neutral-800 overflow-hidden">
                    {product.images?.[0] ? (
                      <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="w-16 h-16 text-neutral-300 dark:text-neutral-600" />
                      </div>
                    )}
                  </div>
                  {product.images && product.images.length > 1 && (
                    <div className="grid grid-cols-4 gap-2">
                      {product.images.slice(1, 5).map((img, i) => (
                        <img key={i} src={img} alt="" className="aspect-square rounded-lg object-cover cursor-pointer hover:opacity-80 transition-opacity" />
                      ))}
                    </div>
                  )}
                </div>

                {/* Details */}
                <div className="lg:col-span-2 space-y-6">
                  <div>
                    <h3 className="font-semibold text-neutral-900 dark:text-white mb-3">توضیحات</h3>
                    <p className="text-neutral-600 dark:text-neutral-400 whitespace-pre-wrap">{product.description || 'توضیحات موجود نیست'}</p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="bg-neutral-50 dark:bg-neutral-800/50 rounded-xl p-4">
                      <h4 className="font-medium text-neutral-900 dark:text-white mb-3">مشخصات اصلی</h4>
                      <dl className="space-y-2 text-sm">
                        <div className="flex justify-between"><dt className="text-neutral-500 dark:text-neutral-400">SKU</dt><dd className="font-mono font-medium text-neutral-900 dark:text-white">{product.sku}</dd></div>
                        <div className="flex justify-between"><dt className="text-neutral-500 dark:text-neutral-400">بارکد</dt><dd className="font-mono font-medium text-neutral-900 dark:text-white">{product.barcode || '—'}</dd></div>
                        <div className="flex justify-between"><dt className="text-neutral-500 dark:text-neutral-400">دسته‌بندی</dt><dd className="font-medium text-neutral-900 dark:text-white">{product.category?.name || '—'}</dd></div>
                        <div className="flex justify-between"><dt className="text-neutral-500 dark:text-neutral-400">برند</dt><dd className="font-medium text-neutral-900 dark:text-white">{product.brand?.name || '—'}</dd></div>
                        <div className="flex justify-between"><dt className="text-neutral-500 dark:text-neutral-400">واحد</dt><dd className="font-medium text-neutral-900 dark:text-white">{product.unit?.name || 'عدد'}</dd></div>
                        <div className="flex justify-between"><dt className="text-neutral-500 dark:text-neutral-400">وزن</dt><dd className="font-medium text-neutral-900 dark:text-white">{product.weight ? `${product.weight} گرم` : '—'}</dd></div>
                      </dl>
                    </div>

                    <div className="bg-neutral-50 dark:bg-neutral-800/50 rounded-xl p-4">
                      <h4 className="font-medium text-neutral-900 dark:text-white mb-3">قیمت‌گذاری</h4>
                      <dl className="space-y-2 text-sm">
                        <div className="flex justify-between"><dt className="text-neutral-500 dark:text-neutral-400">قیمت فروش</dt><dd className="font-mono font-medium text-success-600 dark:text-success-400">{formatCurrency(product.sale_price || 0)}</dd></div>
                        <div className="flex justify-between"><dt className="text-neutral-500 dark:text-neutral-400">قیمت خرید</dt><dd className="font-mono font-medium text-warning-600 dark:text-warning-400">{formatCurrency(product.cost_price || 0)}</dd></div>
                        <div className="flex justify-between"><dt className="text-neutral-500 dark:text-neutral-400">قیمت مقایسه</dt><dd className="font-mono font-medium text-neutral-900 dark:text-white">{formatCurrency(product.compare_at_price || 0) || '—'}</dd></div>
                        <div className="flex justify-between"><dt className="text-neutral-500 dark:text-neutral-400">نرخ مالیات</dt><dd className="font-medium text-neutral-900 dark:text-white">{product.tax_rate || 0}%</dd></div>
                        <div className="flex justify-between"><dt className="text-neutral-500 dark:text-neutral-400">شامل مالیات</dt><dd className="font-medium text-neutral-900 dark:text-white">{product.tax_included ? 'بله' : 'خیر'}</dd></div>
                        <div className="flex justify-between"><dt className="text-neutral-500 dark:text-neutral-400">ارز</dt><dd className="font-medium text-neutral-900 dark:text-white">{product.currency || 'IRR'}</dd></div>
                      </dl>
                    </div>
                  </div>

                  {product.variants && product.variants.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-neutral-900 dark:text-white mb-3">تنوع‌ها</h3>
                      <div className="overflow-x-auto">
                        <table className="table">
                          <thead>
                            <tr>
                              <th>نام</th>
                              <th>SKU</th>
                              <th>ویژگی‌ها</th>
                              <th className="hidden sm:table-cell">قیمت فروش</th>
                              <th className="hidden sm:table-cell">موجودی</th>
                              <th className="w-16"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {product.variants.map((v: any) => (
                              <tr key={v.id}>
                                <td className="font-medium text-neutral-900 dark:text-white">{v.name}</td>
                                <td className="font-mono text-sm text-neutral-700 dark:text-neutral-300">{v.sku}</td>
                                <td>
                                  <div className="flex flex-wrap gap-1">
                                    {Object.entries(v.attributes || {}).map(([k, val]) => (
                                      <Badge key={k} variant="neutral" className="text-xs">{k}: {val as string}</Badge>
                                    ))}
                                  </div>
                                </td>
                                <td className="hidden sm:table-cell font-mono text-sm text-success-600 dark:text-success-400">{formatCurrency(v.sale_price || 0)}</td>
                                <td className={`hidden sm:table-cell font-mono text-sm ${v.current_stock === 0 ? 'text-danger-600' : v.current_stock <= (v.min_stock || 0) ? 'text-warning-600' : 'text-neutral-900'} dark:text-neutral-300`}>{v.current_stock}</td>
                                <td>
                                  <Dropdown
                                    trigger={<button className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"><MoreVertical className="w-4 h-4" /></button>}
                                    items={getRowDropdownItems(v)}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </TabPanel>

            {/* Variants Tab */}
            <TabPanel value="variants" className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-neutral-900 dark:text-white">لیست تنوع‌ها</h3>
                {product.type === 'variant' && hasPermission('product', 'update') && (
                  <Button size="sm" variant="outline">
                    <Plus className="w-4 h-4 ml-1" />
                    افزودن تنوع
                  </Button>
                )}
              </div>
              {product.variants?.length ? (
                <div className="overflow-x-auto">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>تصویر</th>
                        <th>نام</th>
                        <th>SKU</th>
                        <th>ویژگی‌ها</th>
                        <th className="hidden sm:table-cell">قیمت خرید</th>
                        <th className="hidden sm:table-cell">قیمت فروش</th>
                        <th className="hidden md:table-cell">موجودی</th>
                        <th className="w-16"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {product.variants.map((v: any) => (
                        <tr key={v.id}>
                          <td>
                            <div className="w-12 h-12 rounded-lg bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center overflow-hidden">
                              {v.images?.[0] ? <img src={v.images[0]} alt="" className="w-full h-full object-cover" /> : <Tag className="w-5 h-5 text-neutral-400" />}
                            </div>
                          </td>
                          <td className="font-medium text-neutral-900 dark:text-white">{v.name}</td>
                          <td className="font-mono text-sm text-neutral-700 dark:text-neutral-300">{v.sku}</td>
                          <td>
                            <div className="flex flex-wrap gap-1">
                              {Object.entries(v.attributes || {}).map(([k, val]) => (
                                <Badge key={k} variant="neutral" className="text-xs">{k}: {val as string}</Badge>
                              ))}
                            </div>
                          </td>
                          <td className="hidden sm:table-cell font-mono text-sm text-warning-600 dark:text-warning-400">{formatCurrency(v.cost_price || 0)}</td>
                          <td className="hidden sm:table-cell font-mono text-sm text-success-600 dark:text-success-400">{formatCurrency(v.sale_price || 0)}</td>
                          <td className={`hidden md:table-cell font-mono text-sm ${v.current_stock === 0 ? 'text-danger-600' : v.current_stock <= (v.min_stock || 0) ? 'text-warning-600' : 'text-neutral-900'} dark:text-neutral-300`}>{v.current_stock}</td>
                          <td>
                            <Dropdown
                              trigger={<button className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"><MoreVertical className="w-4 h-4" /></button>}
                              items={getRowDropdownItems(v)}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Tag className="w-16 h-16 text-neutral-300 dark:text-neutral-600 mx-auto mb-4" />
                  <p className="text-neutral-500 dark:text-neutral-400">هیچ تنوعی تعریف نشده</p>
                </div>
              )}
            </TabPanel>

            {/* Stock Tab */}
            <TabPanel value="stock" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 card p-5">
                  <h3 className="font-semibold text-neutral-900 dark:text-white mb-4">موجودی بر اساس انبار</h3>
                  {product.stock_by_warehouse?.length ? (
                    <div className="overflow-x-auto">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>انبار</th>
                            <th className="hidden sm:table-cell">موجودی فعلی</th>
                            <th className="hidden sm:table-cell">رزرو شده</th>
                            <th className="hidden md:table-cell">در دسترس</th>
                            <th className="hidden lg:table-cell">حداقل</th>
                            <th className="hidden lg:table-cell">حداکثر</th>
                          </tr>
                        </thead>
                        <tbody>
                          {product.stock_by_warehouse.map((s: any) => (
                            <tr key={s.warehouse_id}>
                              <td className="font-medium text-neutral-900 dark:text-white">{s.warehouse?.name}</td>
                              <td className="hidden sm:table-cell font-mono text-sm text-neutral-900 dark:text-white">{s.current_stock}</td>
                              <td className="hidden sm:table-cell font-mono text-sm text-neutral-600 dark:text-neutral-400">{s.reserved_stock || 0}</td>
                              <td className="hidden md:table-cell font-mono text-sm text-success-600 dark:text-success-400">{s.available_stock || s.current_stock - (s.reserved_stock || 0)}</td>
                              <td className="hidden lg:table-cell font-mono text-sm text-neutral-600 dark:text-neutral-400">{s.min_stock || 0}</td>
                              <td className="hidden lg:table-cell font-mono text-sm text-neutral-600 dark:text-neutral-400">{s.max_stock || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-neutral-500 dark:text-neutral-400 text-center py-8">داده‌های انبار موجود نیست</p>
                  )}
                </div>

                <div className="space-y-4">
                  <div className="card p-5">
                    <h3 className="font-semibold text-neutral-900 dark:text-white mb-4">تنظیمات موجودی</h3>
                    <dl className="space-y-3 text-sm">
                      <div className="flex justify-between"><dt className="text-neutral-500 dark:text-neutral-400">رصد موجودی</dt><dd className="font-medium">{product.track_stock ? 'بله' : 'خیر'}</dd></div>
                      <div className="flex justify-between"><dt className="text-neutral-500 dark:text-neutral-400">موجودی فعلی</dt><dd className="font-mono font-medium">{formatNumber(product.current_stock || 0)}</dd></div>
                      <div className="flex justify-between"><dt className="text-neutral-500 dark:text-neutral-400">حداقل موجودی</dt><dd className="font-mono font-medium">{formatNumber(product.min_stock || 0)}</dd></div>
                      <div className="flex justify-between"><dt className="text-neutral-500 dark:text-neutral-400">حداکثر موجودی</dt><dd className="font-mono font-medium">{product.max_stock ? formatNumber(product.max_stock) : '—'}</dd></div>
                      <div className="flex justify-between"><dt className="text-neutral-500 dark:text-neutral-400">نقطه سفارش مجدد</dt><dd className="font-mono font-medium">{product.reorder_point ? formatNumber(product.reorder_point) : '—'}</dd></div>
                      <div className="flex justify-between"><dt className="text-neutral-500 dark:text-neutral-400">مقدار سفارش مجدد</dt><dd className="font-mono font-medium">{product.reorder_qty ? formatNumber(product.reorder_qty) : '—'}</dd></div>
                      <div className="flex justify-between"><dt className="text-neutral-500 dark:text-neutral-400">انبار پیش‌فرض</dt><dd className="font-medium">{product.default_warehouse?.name || '—'}</dd></div>
                      <div className="flex justify-between"><dt className="text-neutral-500 dark:text-neutral-400">مجوز سفارش.backorder</dt><dd className="font-medium">{product.allow_backorder ? 'بله' : 'خیر'}</dd></div>
                      <div className="flex justify-between"><dt className="text-neutral-500 dark:text-neutral-400">رصد سریال</dt><dd className="font-medium">{product.serial_tracked ? 'بله' : 'خیر'}</dd></div>
                      <div className="flex justify-between"><dt className="text-neutral-500 dark:text-neutral-400">رصد بچ</dt><dd className="font-medium">{product.batch_tracked ? 'بله' : 'خیر'}</dd></div>
                      <div className="flex justify-between"><dt className="text-neutral-500 dark:text-neutral-400">رصد انقضا</dt><dd className="font-medium">{product.expiry_tracked ? 'بله' : 'خیر'}</dd></div>
                    </dl>
                  </div>

                  <div className="card p-5 border-warning-200 dark:border-warning-800">
                    <h3 className="font-semibold text-neutral-900 dark:text-white mb-3 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-warning-600" />
                      هشدارهای موجودی
                    </h3>
                    <div className="space-y-2 text-sm">
                      {product.current_stock === 0 && (
                        <div className="p-3 rounded-lg bg-danger-50 dark:bg-danger-900/20 text-danger-700 dark:text-danger-300">
                          <strong>موجودی تمام شده!</strong> این کالا در انبار وجود ندارد.
                        </div>
                      )}
                      {product.min_stock > 0 && product.current_stock <= product.min_stock && product.current_stock > 0 && (
                        <div className="p-3 rounded-lg bg-warning-50 dark:bg-warning-900/20 text-warning-700 dark:text-warning-300">
                          <strong>موجودی کم!</strong> کمتر از حداقل ({product.min_stock}) است.
                        </div>
                      )}
                      {product.reorder_point && product.current_stock <= product.reorder_point && (
                        <div className="p-3 rounded-lg bg-info-50 dark:bg-info-900/20 text-info-700 dark:text-info-300">
                          <strong>نقطه سفارش مجدد فرا رسیده!</strong> پیشنهاد می‌شود سفارش خرید ثبت کنید.
                        </div>
                      )}
                      {!product.current_stock === 0 && product.min_stock > 0 && product.current_stock > product.min_stock && (!product.reorder_point || product.current_stock > product.reorder_point) && (
                        <div className="p-3 rounded-lg bg-success-50 dark:bg-success-900/20 text-success-700 dark:text-success-300">
                          موجودی در حد مناسب است.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </TabPanel>

            {/* Pricing Tab */}
            <TabPanel value="pricing" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="card p-5">
                  <h3 className="font-semibold text-neutral-900 dark:text-white mb-4">قیمت‌های اصلی</h3>
                  <dl className="space-y-4">
                    <div>
                      <dt className="text-sm text-neutral-500 dark:text-neutral-400">قیمت فروش</dt>
                      <dd className="text-2xl font-bold text-success-600 dark:text-success-400 mt-1">{formatCurrency(product.sale_price || 0)}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-neutral-500 dark:text-neutral-400">قیمت خرید</dt>
                      <dd className="text-2xl font-bold text-warning-600 dark:text-warning-400 mt-1">{formatCurrency(product.cost_price || 0)}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-neutral-500 dark:text-neutral-400">قیمت مقایسه (خط خورده)</dt>
                      <dd className="text-2xl font-bold text-neutral-900 dark:text-white mt-1">{formatCurrency(product.compare_at_price || 0) || 'تنظیم نشده'}</dd>
                    </div>
                    <div className="pt-4 border-t border-neutral-200 dark:border-neutral-800">
                      <dt className="text-sm text-neutral-500 dark:text-neutral-400">حاشیه سود</dt>
                      <dd className="text-2xl font-bold text-primary-600 dark:text-primary-400 mt-1">
                        {product.cost_price && product.sale_price && product.cost_price > 0
                          ? `${(((product.sale_price - product.cost_price) / product.cost_price) * 100).toFixed(1)}%`
                          : '—'}
                      </dd>
                    </div>
                  </dl>
                </div>

                <div className="card p-5">
                  <h3 className="font-semibold text-neutral-900 dark:text-white mb-4">تنظیمات مالیاتی</h3>
                  <dl className="space-y-3 text-sm">
                    <div className="flex justify-between"><dt className="text-neutral-500 dark:text-neutral-400">نرخ مالیات</dt><dd className="font-medium">{product.tax_rate || 0}%</dd></div>
                    <div className="flex justify-between"><dt className="text-neutral-500 dark:text-neutral-400">شامل مالیات</dt><dd className="font-medium">{product.tax_included ? 'بله' : 'خیر'}</dd></div>
                    <div className="flex justify-between"><dt className="text-neutral-500 dark:text-neutral-400">ارز</dt><dd className="font-medium">{product.currency || 'IRR'}</dd></div>
                    <div className="flex justify-between"><dt className="text-neutral-500 dark:text-neutral-400">قیمت با مالیات</dt><dd className="font-mono font-medium text-success-600 dark:text-success-400">
                      {formatCurrency((product.sale_price || 0) * (1 + (product.tax_rate || 0) / 100))}
                    </dd></div>
                  </dl>
                </div>
              </div>

              {product.variants && product.variants.length > 1 && (
                <div className="card p-5">
                  <h3 className="font-semibold text-neutral-900 dark:text-white mb-4">قیمت تنوع‌ها</h3>
                  <div className="overflow-x-auto">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>تنوع</th>
                          <th className="hidden sm:table-cell">قیمت خرید</th>
                          <th className="hidden sm:table-cell">قیمت فروش</th>
                          <th className="hidden md:table-cell">قیمت مقایسه</th>
                          <th className="hidden lg:table-cell">حاشیه سود</th>
                        </tr>
                      </thead>
                      <tbody>
                        {product.variants.map((v: any) => (
                          <tr key={v.id}>
                            <td className="font-medium text-neutral-900 dark:text-white">{v.name}</td>
                            <td className="hidden sm:table-cell font-mono text-sm text-warning-600 dark:text-warning-400">{formatCurrency(v.cost_price || 0)}</td>
                            <td className="hidden sm:table-cell font-mono text-sm text-success-600 dark:text-success-400">{formatCurrency(v.sale_price || 0)}</td>
                            <td className="hidden md:table-cell font-mono text-sm text-neutral-600 dark:text-neutral-400">{formatCurrency(v.compare_at_price || 0) || '—'}</td>
                            <td className="hidden lg:table-cell font-medium {v.cost_price && v.sale_price && v.cost_price > 0 && ((v.sale_price - v.cost_price) / v.cost_price) < 0.2 ? 'text-danger-600' : 'text-success-600'} dark:text-neutral-300">
                              {v.cost_price && v.sale_price && v.cost_price > 0
                                ? `${(((v.sale_price - v.cost_price) / v.cost_price) * 100).toFixed(1)}%`
                                : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </TabPanel>

            {/* Movements Tab */}
            <TabPanel value="movements" className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-neutral-900 dark:text-white">حرکات انبار</h3>
                <Button variant="outline" size="sm">
                  <Download className="w-4 h-4 ml-1" />
                  خروجی اکسل
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>تاریخ</th>
                      <th>نوع</th>
                      <th className="hidden sm:table-cell">انبار</th>
                      <th className="hidden md:table-cell">مقدار</th>
                      <th className="hidden lg:table-cell">موجودی قبل</th>
                      <th className="hidden lg:table-cell">موجودی بعد</th>
                      <th className="hidden xl:table-cell">مرجع</th>
                      <th className="w-16"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {product.movements?.slice(0, 20).map((m: any, i: number) => (
                      <tr key={i}>
                        <td className="text-sm text-neutral-600 dark:text-neutral-400">{formatDate(m.created_at)}</td>
                        <td>
                          <Badge variant={
                            m.type === 'receive' ? 'success' :
                            m.type === 'issue' ? 'danger' :
                            m.type === 'transfer' ? 'primary' :
                            m.type === 'adjustment' ? 'warning' :
                            'neutral'
                          } className="text-xs">
                            {m.type === 'receive' ? 'دریافت' : m.type === 'issue' ? 'صدور' : m.type === 'transfer' ? 'انتقال' : m.type === 'adjustment' ? 'تنظیم' : m.type}
                          </Badge>
                        </td>
                        <td className="hidden sm:table-cell text-sm text-neutral-600 dark:text-neutral-400">{m.warehouse?.name}</td>
                        <td className="hidden md:table-cell font-mono text-sm {m.quantity > 0 ? 'text-success-600' : 'text-danger-600'} dark:text-neutral-300">{m.quantity > 0 ? '+' : ''}{m.quantity}</td>
                        <td className="hidden lg:table-cell font-mono text-sm text-neutral-600 dark:text-neutral-400">{m.previous_stock}</td>
                        <td className="hidden lg:table-cell font-mono text-sm text-neutral-900 dark:text-white">{m.new_stock}</td>
                        <td className="hidden xl:table-cell text-sm text-neutral-600 dark:text-neutral-400">{m.reference_type}: {m.reference_number}</td>
                        <td>
                          <button className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors">
                            <Eye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {product.movements && product.movements.length > 20 && (
                <div className="text-center">
                  <Button variant="outline">نمایش همه حرکات</Button>
                </div>
              )}
            </TabPanel>

            {/* Documents Tab */}
            <TabPanel value="documents" className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-neutral-900 dark:text-white">اسناد و فایل‌ها</h3>
                <Button variant="outline" size="sm">
                  <Upload className="w-4 h-4 ml-1" />
                  آپلود سند
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {product.documents?.map((doc: any) => (
                  <div key={doc.id} className="card p-4 hover:shadow-lg transition-shadow">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
                        {doc.type === 'pdf' && <FileText className="w-6 h-6 text-danger-600" />}
                        {doc.type === 'image' && <Image className="w-6 h-6 text-primary-600" />}
                        {doc.type === 'doc' && <FileText className="w-6 h-6 text-primary-600" />}
                        {doc.type === 'xls' && <FileText className="w-6 h-6 text-success-600" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-neutral-900 dark:text-white truncate">{doc.name}</p>
                        <p className="text-sm text-neutral-500 dark:text-neutral-400">{doc.size} • {formatDate(doc.created_at)}</p>
                      </div>
                      <Dropdown
                        trigger={<button className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"><MoreVertical className="w-4 h-4" /></button>}
                        items={[
                          { label: 'دانلود', icon: Download, onClick: () => {} },
                          { label: 'مشاهده', icon: Eye, onClick: () => {} },
                          { type: 'divider' },
                          { label: 'حذف', icon: Trash2, className: 'text-danger-600', onClick: () => {} },
                        ]}
                      />
                    </div>
                  </div>
                ))}
              </div>
              {!product.documents?.length && (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 text-neutral-300 dark:text-neutral-600 mx-auto mb-4" />
                  <p className="text-neutral-500 dark:text-neutral-400">هیچ سندی آپلود نشده</p>
                </div>
              )}
            </TabPanel>

            {/* History Tab */}
            <TabPanel value="history" className="space-y-4">
              <h3 className="font-semibold text-neutral-900 dark:text-white">تاریخچه تغییرات</h3>
              <div className="space-y-4">
                {product.audit_logs?.map((log: any) => (
                  <motion.div
                    key={log.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="card p-4"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
                        <User className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <span className="font-medium text-neutral-900 dark:text-white">{log.user?.full_name || 'سیستم'}</span>
                          <Badge variant={
                            log.action === 'create' ? 'success' :
                            log.action === 'update' ? 'primary' :
                            log.action === 'delete' ? 'danger' : 'neutral'
                          } className="text-xs">
                            {log.action === 'create' ? 'ایجاد' : log.action === 'update' ? 'به‌روزرسانی' : log.action === 'delete' ? 'حذف' : log.action}
                          </Badge>
                          <span className="text-sm text-neutral-500 dark:text-neutral-400">{formatDate(log.created_at)}</span>
                        </div>
                        {log.changes && Object.keys(log.changes).length > 0 && (
                          <div className="text-sm text-neutral-600 dark:text-neutral-400">
                            {Object.entries(log.changes).map(([field, change]: any) => (
                              <div key={field} className="flex items-center gap-2">
                                <span className="font-medium">{field}:</span>
                                <span className="text-danger-600 line-through">{change.old}</span>
                                <span className="text-success-600">→</span>
                                <span className="text-success-600 font-medium">{change.new}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {log.description && (
                          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">{log.description}</p>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
              {!product.audit_logs?.length && (
                <div className="text-center py-8">
                  <Clock className="w-12 h-12 text-neutral-300 dark:text-neutral-600 mx-auto mb-3" />
                  <p className="text-neutral-500 dark:text-neutral-400">تاریخچه‌ای موجود نیست</p>
                </div>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </motion.div>

      {/* Delete Modal */}
      <Modal
        isOpen={modals['delete-confirm']}
        onClose={() => { closeModal('delete-confirm'); setDeletedId(null) }}
        title="حذف کالا"
        size="sm"
      >
        <p className="text-neutral-600 dark:text-neutral-400">
          آیا از حذف <strong>{product?.name}</strong> مطمئن هستید؟ این عملیات قابل بازگشت نیست.
        </p>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={() => { closeModal('delete-confirm'); setDeletedId(null) }}>انصراف</Button>
          <Button variant="danger" onClick={() => deletedId && deleteMutation.mutate(deletedId)} disabled={deleteMutation.isPending}>
            {deleteMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                در حال حذف...
              </>
            ) : (
              'حذف'
            )}
          </Button>
        </div>
      </Modal>

      {/* Variant Delete Modal */}
      <Modal
        isOpen={modals['delete-confirm'] && !!deletedId}
        onClose={() => { closeModal('delete-confirm'); setDeletedId(null) }}
        title="حذف تنوع"
        size="sm"
      >
        <p className="text-neutral-600 dark:text-neutral-400">
          آیا از حذف این تنوع مطمئن هستید؟
        </p>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={() => { closeModal('delete-confirm'); setDeletedId(null) }}>انصراف</Button>
          <Button variant="danger" onClick={() => deletedId && deleteMutation.mutate(deletedId)} disabled={deleteMutation.isPending}>
            {deleteMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                در حال حذف...
              </>
            ) : (
              'حذف'
            )}
          </Button>
        </div>
      </Modal>
    </div>
  )
}